/**
 * StageAcknowledgment — "I have reviewed the content on this stage"
 * checkbox (Item 2 of the epi Guidelines UI Compliance addendum).
 *
 * Rendered immediately above each stage's Next / Freeze control.
 * Its ``checked`` value AND-s with the caller's existing
 * freeze-gate conditions to produce the button's disabled state —
 * the ack alone never unlocks the gate; the gate alone never
 * advances the stage. Both must agree.
 *
 * State model:
 *   - ``initialAcknowledgedAt`` seeds the checkbox from whatever the
 *     server has persisted (an ISO string when previously checked,
 *     null when unset). Checking POSTs with acknowledged=true;
 *     unchecking POSTs with acknowledged=false. Either path is
 *     best-effort — a transient server error leaves the checkbox
 *     un-flipped and surfaces the message via onError.
 *
 * Disabling cases:
 *   - Saving in flight (double-click guard).
 *   - Stage already frozen (no further acks needed — server would
 *     accept them but the UX would be weird; caller passes
 *     ``disabled`` in that case).
 */
import React, { useEffect, useState } from 'react';
import { CheckSquare, Square, Loader2 } from 'lucide-react';
import * as api from '@/api/client';


interface Props {
  matterId: string;
  stage: api.AckStage;
  /** ISO timestamp persisted by the server, or null when unacked.
   *  The checkbox renders checked whenever this is a truthy string. */
  initialAcknowledgedAt: string | null;
  /** Called after a successful save with the new ISO timestamp
   *  (truthy) or null (cleared) so the parent can keep its local
   *  copy in sync without a full matter refresh. */
  onChange: (nextAt: string | null) => void;
  /** Stage freezing / other conditions caller wants to block the
   *  checkbox on (e.g. matter frozen). Defaults false. */
  disabled?: boolean;
  /** Optional error surfacing. */
  onError?: (msg: string) => void;
}


export default function StageAcknowledgment({
  matterId, stage, initialAcknowledgedAt, onChange, disabled, onError,
}: Props) {
  const [checked, setChecked] = useState<boolean>(!!initialAcknowledgedAt);
  const [saving, setSaving] = useState(false);

  // Sync local state when the initial prop changes (e.g. matter
  // refresh re-hydrates the persisted value). Only runs on external
  // change to avoid fighting the user's click.
  useEffect(() => {
    setChecked(!!initialAcknowledgedAt);
  }, [initialAcknowledgedAt]);

  const handleToggle = async () => {
    if (saving || disabled) return;
    const next = !checked;
    // Optimistic flip — the server round-trip is short and the
    // failure path below rolls back if the POST rejects.
    setChecked(next);
    setSaving(true);
    try {
      const resp = await api.setStageAcknowledged(matterId, stage, next);
      onChange(resp.acknowledged_at);
    } catch (e: any) {
      // Rollback on error so the checkbox matches server truth.
      setChecked(!next);
      onError?.(
        e?.response?.data?.detail
        || 'Could not save acknowledgment — please try again',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <label
      data-testid={`stage-acknowledgment-${stage}`}
      data-acknowledged={checked ? 'true' : 'false'}
      className={
        'inline-flex items-center gap-2 text-xs cursor-pointer select-none '
        + (disabled ? 'opacity-50 cursor-not-allowed ' : '')
        + 'text-zinc-700 hover:text-zinc-900'
      }
      title={checked
        ? 'Review acknowledged — click to clear'
        : 'Check this after you have reviewed the content on this stage'}
    >
      <input
        type="checkbox"
        data-testid={`stage-acknowledgment-${stage}-input`}
        checked={checked}
        onChange={handleToggle}
        disabled={saving || disabled}
        className="sr-only"
      />
      {saving ? (
        <Loader2 size={14} className="animate-spin text-zinc-500" />
      ) : checked ? (
        <CheckSquare size={14} className="text-[#002FA7]" />
      ) : (
        <Square size={14} className="text-zinc-400" />
      )}
      <span>I have reviewed the content on this stage.</span>
    </label>
  );
}
